import type { BillingOverview } from "@contract/firm/billing";
import type { Me } from "@contract/firm/me";
import { Badge, Button, Group, NumberInput, Text } from "@mantine/core";
import * as React from "react";
import { SecondaryButton } from "../../ui/buttons";
import { Notice } from "../../ui/Notice";
import { SectionCard } from "../../ui/SectionCard";
import { PANEL_COLOR, PANEL_FONT } from "../../ui/tokens";
import {
  BEFORE_TAX,
  creditsCell,
  floorLine,
  individualPrice,
  minSeats,
  priceCell,
  seatChangeLine,
  teamPriceFor,
  upgradeLine,
  type BillingState,
} from "../planBillingModel";
import type { usePlanChange } from "./usePlanChange";

type Change = ReturnType<typeof usePlanChange>;

/** One row of the comparison: its label, then the Individual and Team cells. */
function Row({ label, individual, team }: { label: string; individual: React.ReactNode; team: React.ReactNode }) {
  return (
    <>
      <Text fw={600} lh={1.25}>
        {label}
      </Text>
      <div className="sb-plan-cell">{individual}</div>
      <div className="sb-plan-cell">{team}</div>
    </>
  );
}

/** A plan's name, with "Current plan" under the one the firm is on. */
function PlanName({ name, current }: { name: string; current: boolean }) {
  return (
    <Group gap={5} justify="center" wrap="nowrap">
      <Text lh={1.25}>{name}</Text>
      {current ? <Badge>Current plan</Badge> : null}
    </Group>
  );
}

/**
 * Change Your Plan (frame 15199:6383, Dan 2026-10-04): Individual and Team side by side
 * (seats, price, AI credits), then the actions for this firm's state. A trial buys either
 * through Paddle's checkout, monthly only (annual is off the page, Dan 2026-10-04; a live
 * annual subscription still shows its own /yr prices); Individual upgrades to Team; Team
 * changes its seats. An
 * upgrade or a seat change applies at once, so it's priced and confirmed first. "Manage
 * billing" (Paddle's portal: card, invoices, cancelling) sits under the current plan.
 */
export function PlanCompareCard({ me, overview, state, change }: { me: Me; overview: BillingOverview; state: BillingState; change: Change }) {
  const floor = minSeats(me.seats, overview.includedSeats);
  const onTeam = overview.plan === "team" && overview.subscribed;
  const [seats, setSeats] = React.useState<number>(onTeam ? me.seats.count : floor);
  const cycle = overview.cycle ?? "monthly";
  const seatsEditable = state === "trial" || state === "individual" || state === "team";
  const teamSeats = seatsEditable ? seats : onTeam ? me.seats.count : overview.includedSeats;
  const tooFew = seatsEditable && seats < floor;
  const currentIndividual = overview.subscribed && !onTeam;
  const { busy, pending } = change;

  const seatBox = seatsEditable ? (
    <NumberInput
      aria-label="Team seats"
      size="xs"
      w={90}
      // The theme hides NumberInput controls; this box shows them (Dan 2026-10-04).
      hideControls={false}
      min={floor}
      max={1000}
      value={seats}
      onChange={(v) => {
        setSeats(typeof v === "number" ? v : floor);
        change.dismiss();
      }}
      styles={{ input: { textAlign: "center" } }}
    />
  ) : (
    <Text lh={1.25}>{teamSeats}</Text>
  );

  const manage = (
    <SecondaryButton fullWidth onClick={() => void change.openPortal()}>
      Manage billing
    </SecondaryButton>
  );

  const actions = ((): { individual: React.ReactNode; team: React.ReactNode } | null => {
    switch (state) {
      case "trial":
        return {
          individual: (
            <SecondaryButton fullWidth loading={busy} onClick={() => void change.choose("tier1", cycle, 1)}>
              Choose Individual
            </SecondaryButton>
          ),
          team: (
            <Button fullWidth loading={busy} disabled={tooFew} onClick={() => void change.choose("team", cycle, seats)}>
              Choose Team
            </Button>
          ),
        };
      case "individual":
        return {
          individual: manage,
          team: (
            <Button fullWidth loading={busy && !pending} disabled={tooFew || pending !== null} onClick={() => void change.reviewUpgrade(cycle, seats)}>
              Upgrade to Team plan
            </Button>
          ),
        };
      case "team":
        return {
          individual: (
            <Text c={PANEL_COLOR.textSecondary} ta="center" lh={1.25}>
              To move back, cancel Team and choose Individual when it ends.
            </Text>
          ),
          team: (
            <Group grow gap={5} wrap="nowrap" w="100%">
              {manage}
              <Button loading={busy && !pending} disabled={tooFew || seats === me.seats.count || pending !== null} onClick={() => void change.reviewSeats(seats)}>
                Update seats
              </Button>
            </Group>
          ),
        };
      case "ending":
        return onTeam ? { individual: null, team: manage } : { individual: manage, team: null };
      default:
        return null;
    }
  })();

  return (
    <SectionCard title="Change Your Plan">
      <div className="sb-plan-table">
        <Row
          label="Plan"
          individual={<PlanName name="Individual" current={currentIndividual} />}
          team={<PlanName name="Team" current={onTeam} />}
        />
        <Row label="Seats" individual={<Text lh={1.25}>1</Text>} team={seatBox} />
        <Row
          label="Price"
          individual={<Text lh={1.25}>{priceCell(individualPrice(overview, cycle), cycle)}</Text>}
          team={<Text lh={1.25}>{priceCell(teamPriceFor(overview, cycle, teamSeats), cycle)}</Text>}
        />
        <Row
          label="AI Credits"
          individual={<Text lh={1.25}>{creditsCell(overview.credits.individual, 1)}</Text>}
          team={<Text lh={1.25}>{creditsCell(overview.credits.teamPerSeat, teamSeats)}</Text>}
        />
        {actions ? <Row label="" individual={actions.individual} team={actions.team} /> : null}
      </div>
      {tooFew ? <Text c={PANEL_COLOR.textSecondary}>{floorLine(me.seats, overview.includedSeats)}</Text> : null}
      <Text c={PANEL_COLOR.textSecondary} fz={PANEL_FONT.small}>
        Prices are {BEFORE_TAX}.
      </Text>
      {pending ? (
        <Notice
          tone="info"
          action={
            <Group gap={5} wrap="nowrap">
              <SecondaryButton size="xs" onClick={change.dismiss}>
                Cancel
              </SecondaryButton>
              <Button size="xs" loading={busy} onClick={() => void change.confirm(me.seats.count)}>
                {pending.kind === "upgrade" ? `Upgrade to Team (${pending.preview.seats} seats)` : `Change to ${pending.preview.seats} seats`}
              </Button>
            </Group>
          }
        >
          {pending.kind === "upgrade" ? upgradeLine(pending.preview) : seatChangeLine(pending.preview)}
        </Notice>
      ) : null}
      {change.error ? (
        <Notice tone="danger" onClose={change.clearError}>
          {change.error}
        </Notice>
      ) : null}
    </SectionCard>
  );
}
